import React from 'react';
import {
  type SerializeArguments,
  type DeserializeArguments,
  type MarkAttributes,
} from './Serializer';
import { type AnnotationKey } from '../../types/aha-editor';
import { Text, type Descendant } from 'slate';
import { isHTMLElementNode } from '../helpers/serializerHelpers';
import MarkSerializer from './MarkSerializer';

class SerializerAnnotation extends MarkSerializer {
  static deserialize({
    el,
    next,
    markAttributes,
  }: DeserializeArguments): Descendant[] | undefined {
    if (!isHTMLElementNode(el) || el.tagName.toLowerCase() !== 'span') return;

    const key = el.dataset.annotationKey;
    if (!key) return;

    const newMarks: MarkAttributes = {};
    const kind = el.dataset.annotationKind || 'comment';

    newMarks[`annotation_${key}`] = key;
    newMarks[`annotation_${key}_kind`] = kind;

    switch (kind) {
      case 'comment': {
        if (el.dataset.annotationResolved === 'true') {
          newMarks[`annotation_${key}_resolved`] = true;
        }
        if (el.dataset.annotationHidden === 'true') {
          newMarks[`annotation_${key}_hidden`] = true;
        }
        break;
      }
      case 'record': {
        const recordType = el.dataset.annotationRecordType;
        const recordId = el.dataset.annotationRecordId;
        if (recordType && recordId) {
          newMarks[`annotation_${key}_recordType`] = recordType;
          newMarks[`annotation_${key}_recordId`] = recordId;
        }
        break;
      }
      case 'prompt': {
        const promptId = el.dataset.annotationPromptId;
        if (promptId) {
          newMarks[`annotation_${key}_promptId`] = promptId;
        }
        break;
      }
    }

    const childAttributes: MarkAttributes = {
      ...markAttributes,
      ...newMarks,
    };

    const children = next(el.childNodes, childAttributes);
    if (children.length === 0) {
      children.push({
        text: '',
        ...childAttributes,
      });
    }

    return children;
  }

  static serialize({
    node,
    options,
    children,
  }: SerializeArguments): React.ReactNode {
    if (!Text.isText(node)) return;

    const annotationKeys = Object.keys(node)
      .map((key) => key.match(/^annotation_(\d+)$/)?.[1])
      .filter(Boolean) as AnnotationKey[];

    if (annotationKeys.length === 0) return;

    return annotationKeys.reduce((acc, key) => {
      const annotation = node[`annotation_${key}`];
      if (!annotation) return acc;
      const annotationKind = node[`annotation_${key}_kind`] ?? 'comment';

      const classNames = ['aha-editor-field__annotation'];
      const url = new URL(`/annotations/${key}`, options.baseURL);
      const params = new URLSearchParams();
      const anchorComponents: Array<string> = [];

      const attributes: Record<string, any> = {
        'data-annotation-key': key,
        'data-annotation-kind': annotationKind,
      };

      params.set('annotation_kind', annotationKind);

      switch (annotationKind) {
        case 'comment': {
          if (node[`annotation_${key}_resolved`] === true) {
            attributes['data-annotation-resolved'] = true;
            classNames.push('aha-editor-field__annotation--resolved');
            // Compatibility with old annotation URLs
            anchorComponents.push('annotation_resolved');
          }
          if (node[`annotation_${key}_hidden`] === true) {
            attributes['data-annotation-hidden'] = true;
            params.set('hidden', 'true');
          }
          break;
        }
        case 'record': {
          const recordType = node[`annotation_${key}_recordType`];
          const recordId = node[`annotation_${key}_recordId`];
          if (recordType && recordId) {
            attributes['data-annotation-record-type'] = recordType;
            params.set('record_type', recordType);
            attributes['data-annotation-record-id'] = recordId;
            params.set('record_id', recordId);
          }
          break;
        }
        case 'prompt': {
          const promptId = node[`annotation_${key}_promptId`];
          if (promptId) {
            attributes['data-annotation-prompt-id'] = promptId;
            params.set('prompt_id', promptId);
          }
          break;
        }
      }

      url.search = params.toString();
      url.hash = anchorComponents.join('/');

      attributes['data-annotation-href'] = url.toString();

      return (
        <span className={classNames.join(' ')} {...attributes}>
          {acc}
        </span>
      );
    }, children);
  }
}

export default SerializerAnnotation;
